import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { tap, finalize, takeUntil } from 'rxjs/operators';
import { ServiceaftaleDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceaftale-dto';
import { OpfoelgningService, ServiceAftaleService } from 'src/app/api/services';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { NotificationType } from 'src/app/notifications/models/notification-models';
import { NotificationService } from 'src/app/notifications/services/notification.service';
import { OpfoelgningDialogComponent } from 'src/app/opfoelgning/components/opfoelgning-dialog/opfoelgning-dialog.component';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { LoaderService } from 'src/app/services/loader.service';
import { OpfoelgningChanged, SignalrService } from 'src/app/services/signalr.service';
import { OpfoelgningAction, OpfoelgningDialogData, OpfoelgningDialogResponse } from '../models';
import { PaginationCacheService } from 'src/app/services/paginationcache.service';
import { OpfoelgningDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/opfoelgning-dto';
import { AuthService } from 'src/app/services/auth.service';
import { inOutAnimation } from 'src/app/common/animations';
import { ServiceaftaleStateEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/serviceaftale-state-enum';
import { OpfoelgningerService } from 'src/app/services/opfoelgninger.service';

@Component({
  selector: 'app-mine-seneste',
  templateUrl: './mine-seneste.component.html',
  styleUrls: ['./mine-seneste.component.scss'],
  animations: [inOutAnimation],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MineSenesteComponent implements OnInit, OnDestroy {
  quickViewOpen: boolean = false;

  globalActiveRowID: string = '';

  displayedColumns: string[] = [
    'produkt',
    'actions',
    'partner',
    'state',
    'okKundeAftaleNr',
    'kundeNavn',
    'adresse',
    'telefon',
    'modifiedOn',
    'createdOn',
  ];

  serviceaftaler: ServiceaftaleDto[] = [];
  selectedaftale: ServiceaftaleDto;

  unsubscribe$ = new Subject<void>();

  dataSource: MatTableDataSource<ServiceaftaleDto> = new MatTableDataSource<ServiceaftaleDto>();

  @ViewChild(MatSort) sort: MatSort;

  canWrite: boolean = false;

  constructor(
    private serviceaftaleService: ServiceAftaleService,
    private loader: LoaderService,
    private errorHandler: ErrorHandlerService,
    private router: Router,
    private dialog: MatDialog,
    private opfoelgningService: OpfoelgningService,
    private notification: NotificationService,
    private signalR: SignalrService,
    private paginationCache: PaginationCacheService,
    private auth: AuthService,
    private opfoelgningerService: OpfoelgningerService,
  ) {
    this.dataSource = new MatTableDataSource();
    this.canWrite = this.auth.canWrite;
  }

  ngOnInit(): void {
    this.loader.show();
    const pagination = this.paginationCache.position;
    if (pagination) {
      this.prevRow = pagination.id;
    }

    this.serviceaftaleService
      .getMineServiceaftaler({ caller: EGIS_SERVICE_CALLER })
      .pipe(
        tap((response: ServiceaftaleDto[]) => {
          this.serviceaftaler = response;
          this.dataSource = new MatTableDataSource(response);
          this.dataSource.sortingDataAccessor = (item, property) => {
            switch (property) {
              case 'partner':
                return item.partner.name;
              case 'produkt':
                return item.serviceAbonnement.product.text;
              case 'state':
                return item.serviceaftaleState.text;
              default:
                return item[property];
            }
          };
          this.dataSource.sort = this.sort;
        }),
        finalize(() => {
          this.loader.hide();
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(
        () => {},
        (error) => {
          this.errorHandler.HandleError(error);
        },
      );

    this.signalR.opfoelgningChanged$.pipe(takeUntil(this.unsubscribe$)).subscribe((event: OpfoelgningChanged) => {
      const serviceaftale = this.serviceaftaler.find((x) => x.serviceAftaleNo == event.entityNo);
      if (serviceaftale) {
        //TODO: [thomas] Update opfoelgning
        //serviceaftale.opfoelgning = event.opfoelgning;

        let index = serviceaftale.opfoelgningList.findIndex((p) => p.id == event.opfoelgning.id);
        if (index >= 0) serviceaftale.opfoelgningList.splice(index, 1, event.opfoelgning);
        else serviceaftale.opfoelgningList.push(event.opfoelgning);

        this.dataSource = new MatTableDataSource(this.serviceaftaler);
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  onRowClick(row: any) {
    this.globalActiveRowID = row.id;
    this.quickViewOpen = true;
    this.selectedaftale = row;
    //this.router.navigate(['/aftale', row.okKundeAftaleNr]);
  }

  onRowDblClick(row: any) {
    this.router.navigate(['/aftale', row.okKundeAftaleNr]);
  }

  getProductIcon(serviceAftale: ServiceaftaleDto) {
    return serviceAftale && serviceAftale.serviceAbonnement && serviceAftale.serviceAbonnement.product
      ? serviceAftale.serviceAbonnement.product.text.toLowerCase()
      : '';
  }

  getStatusIcon(serviceAftale: ServiceaftaleDto) {
    if (serviceAftale.isActive) return 'checkmark';
    //Created-pending vs notactive
    return serviceAftale.serviceaftaleState.state == ServiceaftaleStateEnum.CreatedPending ? 'watch' : 'ban';
  }

  concatAdresseLokation(serviceaftale: ServiceaftaleDto): string {
    const adresse =
      serviceaftale.kunde?.anlaegsadresse?.adressLine == null || serviceaftale.kunde?.anlaegsadresse?.adressLine == undefined
        ? null
        : serviceaftale.kunde?.anlaegsadresse?.adressLine;
    const lokation =
      serviceaftale.kunde?.lokation == null || serviceaftale.kunde?.lokation == undefined ? null : serviceaftale.kunde?.lokation;

    if (adresse && lokation) return `${adresse}\u00A0\u00B7\u00A0${lokation}`;
    if (adresse && !lokation) return adresse;
    if (!adresse && lokation) return lokation;
    return 'N/A';
  }

  closeQuickView() {
    this.quickViewOpen = false;
    this.globalActiveRowID = '';
  }

  opfoelgning(event: Event, serviceaftale: ServiceaftaleDto) {
    event.stopPropagation();

    const data: OpfoelgningDialogData = {
      opfoelgninger: serviceaftale.opfoelgningList,
      entityNo: serviceaftale.serviceAftaleNo,
      okKundeAftaleNr: serviceaftale.okKundeAftaleNr,
    };
    let dialogRef = this.dialog.open(OpfoelgningDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      data,
    });

    dialogRef.afterClosed().subscribe((response: OpfoelgningDialogResponse) => {
      if (response) {
        //TODO: [thomas] Update opfoelgning
        //this.serviceaftaler.find((x) => x.serviceAftaleNo == response.opfoelgning.entityNo).opfoelgning = response.opfoelgning;
      }
    });
  }

  onOpfoelgningChanged(response: OpfoelgningDialogResponse) {
    if (response.action == OpfoelgningAction.Upsert) {
      this.loader.show();

      this.opfoelgningService
        .upsertServiceaftaleOpfoelgning({
          caller: EGIS_SERVICE_CALLER,
          body: {
            id: response.opfoelgning.id,
            note: response.opfoelgning.note,
            deadline: response.opfoelgning.deadline ? new Date(response.opfoelgning.deadline).toOkDateString() : null,
            prioritet: response.opfoelgning.priority,
            entityNo: response.opfoelgning.entityNo,
          },
        })
        .pipe(
          finalize(() => {
            this.loader.hide();
          }),
          takeUntil(this.unsubscribe$),
        )
        .subscribe(
          () => {
            this.notification.success('Opfølgning er gemt.');
          },
          (error) => {
            this.errorHandler.HandleError(error);
          },
        );
    } else if (response.action == OpfoelgningAction.Delete) {
      this.opfoelgningService
        .removeServiceaftaleOpfoelgning({ caller: EGIS_SERVICE_CALLER, id: response.opfoelgning.id })
        .pipe(
          finalize(() => {
            this.loader.hide();
          }),
          takeUntil(this.unsubscribe$),
        )
        .subscribe(
          () => {
            this.notification.success('Opfølgning er fjernet.');
            const sa = this.serviceaftaler.find((x) => x.serviceAftaleNo == response.opfoelgning.entityNo);
            if (sa) {
              let index = sa.opfoelgningList.findIndex((p) => p.id == response.opfoelgning.id);
              if (index >= 0) sa.opfoelgningList.splice(index, 1);
            }
          },
          (error) => {
            this.errorHandler.HandleError(error);
          },
        );
    }
  }

  page: number = 1;
  prevRow: number = null;
  highLight(okKundeAftaleNr: number) {
    var row = this.serviceaftaler.find((p) => p.okKundeAftaleNr == okKundeAftaleNr);
    if (row) this.globalActiveRowID = row.id.toString();
  }

  opfoelgninger(dto: ServiceaftaleDto): string {
    return dto.opfoelgningList.map((o) => o.note).join('\n');
  }

  useropfolgninger(dto: ServiceaftaleDto): Array<OpfoelgningDto> {
    return dto.opfoelgningList.filter((p) => p.user == this.auth.username);
  }

  button(serviceaftale) {
    return this.opfoelgningerService.serviceaftaleState(serviceaftale, this.auth.username);
  }
}
